import { useEffect, useRef, useState, useMemo } from 'react';
import { WeeklySummary, TranslationState } from '@amzn/aet-summaries-kit';

const TRANSLATION_POLL_DELAY_MS = 500;
const TRANSLATION_MAX_RETRIES = 3;

interface UseTranslationPollingOptions {
  summaries: WeeklySummary[];
  onPoll: () => void;
  resetDeps: unknown[]; // Dependencies that trigger retry count reset (e.g., userAlias, currentPage)
}

interface UseTranslationPollingResult {
  isPollingTranslation: boolean;
  hasExhaustedTranslationRetries: boolean;
  hasPendingTranslation: boolean;
}

export const useTranslationPolling = ({
  summaries,
  onPoll,
  resetDeps,
}: UseTranslationPollingOptions): UseTranslationPollingResult => {
  const [retryCount, setRetryCount] = useState(0);
  const [isPolling, setIsPolling] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRun = useRef(true);

  const hasPendingTranslation = useMemo(
    () =>
      summaries.some(
        summary => summary.translationState === TranslationState.PENDING
      ),
    [summaries]
  );

  const hasExhaustedRetries = retryCount >= TRANSLATION_MAX_RETRIES;

  // Polling effect
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (hasPendingTranslation && !hasExhaustedRetries) {
      setIsPolling(true);
      timeoutRef.current = setTimeout(() => {
        setRetryCount(c => c + 1);
        onPoll();
      }, TRANSLATION_POLL_DELAY_MS);
    } else if (!hasPendingTranslation && isPolling) {
      setIsPolling(false);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [
    retryCount,
    hasPendingTranslation,
    hasExhaustedRetries,
    isPolling,
    onPoll,
  ]);

  // Reset on dependency changes (skip first run to avoid overwriting initial polling state)
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    setRetryCount(0);
    setIsPolling(false);
  }, resetDeps);

  return {
    isPollingTranslation: isPolling,
    hasExhaustedTranslationRetries: hasExhaustedRetries,
    hasPendingTranslation,
  };
};
